import { Flex, Text } from '@chakra-ui/react';
import { Modal, Button, useToast } from '@morressier/alembic';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAcceptForPeerReview } from 'src/api/commands';
import { IDecisionModalProps } from './decisionModalHelper';
import { useSubmissionData } from '../../../contexts/SubmissionDataProvider';
import { SUBMISSION_DETAIL_TABS } from 'src/pages/SubmissionDetails';
import { useSubmissionDetails } from 'src/pages/SubmissionDetails/SubmissionDetailsContext';
import { createEditorialModal } from './EditorialDecisionModalWrapper';
import { EditorialNote, useEditorialDecisionNote } from './EditorialNote';
import { ISubmissionWorkflowState } from '@publishing-engine/domain/models';

export const ProceedModal = createEditorialModal(
  ({ onClose, submissionId, submissionContextId }: IDecisionModalProps) => {
    const toast = useToast();
    const navigate = useNavigate();
    const location = useLocation();

    const { refetchSubmission, organizationId, workflowResponse } =
      useSubmissionData();
    const { setActiveTab } = useSubmissionDetails();
    const currentState = workflowResponse.workflowData
      .currentState as ISubmissionWorkflowState;
    const isRevisionAssessment = currentState === 'revisionAssessment';
    const acceptForPeerReviewMutation = useAcceptForPeerReview(
      submissionId,
      submissionContextId,
      refetchSubmission,
      organizationId
    );

    const { editorNote, setEditorNote, submitEditorNote } =
      useEditorialDecisionNote({
        submissionId,
        submissionContextId,
        organizationId
      });

    const proceedToPeerReview = async () => {
      await acceptForPeerReviewMutation.mutateAsync();
      await submitEditorNote(editorNote);
      setActiveTab(SUBMISSION_DETAIL_TABS.REVIEWS);
      const searchParams = new URLSearchParams(location.search);
      searchParams.set('activeTab', SUBMISSION_DETAIL_TABS.REVIEWS);
      navigate(`${location.pathname}?${searchParams.toString()}`, {
        state: location.state
      });
      onClose();
      toast({
        variant: 'success',
        title: `${
          isRevisionAssessment
            ? 'Revision Assessment Completed'
            : 'Assessment Complete'
        }`,
        description: `${
          isRevisionAssessment
            ? 'Reinvite reviewers for revised manuscript or decide now.'
            : 'Submission will Proceed to Peer Review'
        }`,
        isClosable: true
      });
    };

    return (
      <Modal isOpen onClose={onClose} width="448px">
        <Flex padding={8} gap={4} flexDirection="column">
          <Text textStyle="headingGroup">Proceed To Peer Review?</Text>
          <Text>
            Proceeding with this decision confirms you have reviewed the
            submission against the provided checklist.
          </Text>
          <EditorialNote value={editorNote} onChange={setEditorNote} />

          <Flex gap={2} justifyContent="flex-end">
            <Button onClick={onClose} variant="secondary">
              Cancel
            </Button>
            <Button
              type="submit"
              isLoading={acceptForPeerReviewMutation.isLoading}
              onClick={proceedToPeerReview}
            >
              Proceed
            </Button>
          </Flex>
        </Flex>
      </Modal>
    );
  }
);
